import { useLocale } from 'state/intl';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { isWeb } from 'utils/environment';
import { isCatering, isDelivery } from 'utils/service-mode';

export type CanadaOrderScope = 'canada-delivery' | 'canada-pickup' | 'unaffected';

export type CanadaDeliveryLimitState =
  | { readonly kind: 'clear' }
  | { readonly kind: 'minimum' }
  | { readonly kind: 'maximum' };

interface ResolveCanadaDeliveryLimitStateInput {
  scope: CanadaOrderScope;
  cartPriceTooLow: boolean;
  cartPriceLimitExceeded: boolean;
}

interface ResolveCanUserCheckoutInput extends ResolveCanadaDeliveryLimitStateInput {
  isCartEmpty: boolean;
}

export const resolveCanadaOrderScope = (
  region: string,
  serviceMode: ServiceMode | null,
  isWebPlatform = isWeb
): CanadaOrderScope => {
  if (!isWebPlatform || region !== 'CA' || !serviceMode || isCatering(serviceMode)) {
    return 'unaffected';
  }

  if (isDelivery(serviceMode)) {
    return 'canada-delivery';
  }

  if (serviceMode === ServiceMode.TAKEOUT) {
    return 'canada-pickup';
  }

  return 'unaffected';
};

export const resolveCanadaDeliveryLimitState = ({
  scope,
  cartPriceTooLow,
  cartPriceLimitExceeded,
}: ResolveCanadaDeliveryLimitStateInput): CanadaDeliveryLimitState => {
  if (scope !== 'canada-delivery') {
    return { kind: 'clear' };
  }

  if (cartPriceLimitExceeded) {
    return { kind: 'maximum' };
  }

  if (cartPriceTooLow) {
    return { kind: 'minimum' };
  }

  return { kind: 'clear' };
};

export const resolveCanUserCheckout = ({
  scope,
  isCartEmpty,
  cartPriceTooLow,
  cartPriceLimitExceeded,
}: ResolveCanUserCheckoutInput): boolean =>
  !isCartEmpty && !cartPriceLimitExceeded && (scope !== 'canada-delivery' || !cartPriceTooLow);

export const useCanadaOrderScope = (): CanadaOrderScope => {
  const { region } = useLocale();
  const { serviceMode } = useServiceModeContext();

  return resolveCanadaOrderScope(region, serviceMode);
};

export const useCanadaDeliveryLimitState = ({
  cartPriceTooLow,
  cartPriceLimitExceeded,
}: Omit<ResolveCanadaDeliveryLimitStateInput, 'scope'>): CanadaDeliveryLimitState => {
  const scope = useCanadaOrderScope();

  return resolveCanadaDeliveryLimitState({
    scope,
    cartPriceTooLow,
    cartPriceLimitExceeded,
  });
};
